import { Controller } from "@hotwired/stimulus";
import { actionSource } from "../utils/action_source";
import { hasModifierChord, isReservedArrowChord, logicalArrowStep } from "../utils/arrow_step";
import { setAttributeIfChanged } from "../utils/attribute_write";
import { AuthoredMutationWatcher } from "../utils/authored_mutation_watcher";
import { ownerOf } from "../utils/event_owner";
import { commitField, writeField } from "../utils/field_mirror";
import { inheritsFieldsetDisabled } from "../utils/focus_candidate";
import { isInteractiveHost } from "../utils/interactive_host";
import { ListenerSet } from "../utils/listener_set";
import { MorphRenderWatcher } from "../utils/morph_render_watcher";
import { MoveCounter } from "../utils/move_counter";
import { RovingTabindex, rovingMove } from "../utils/roving_tabindex";
import type { StateReason } from "../utils/state_reason";
import { targetSelector } from "../utils/target_selector";

/** A selection as the group publishes it: the checked radio and the value it submits. */
interface Selection {
  readonly radio: HTMLElement | null;
  readonly value: string;
}

/** The selection of a group with no radio checked. */
const NO_SELECTION: Selection = { radio: null, value: "" };

/** Radio and field attributes whose retained-element changes alter group state. */
const OBSERVED_ATTRIBUTES = [
  "aria-checked",
  "aria-disabled",
  "contenteditable",
  "controls",
  "data-value",
  "disabled",
  "hidden",
  "href",
  "tabindex",
  "type",
  "value",
];

/**
 * Headless, accessible radio-group behavior for **custom** radios.
 *
 * Markup contract (identifier: `stimeo--radio-group`):
 *   <div data-controller="stimeo--radio-group" role="radiogroup" aria-label="Plan">
 *     <div role="radio" aria-checked="true" tabindex="0" data-value="basic"
 *          data-stimeo--radio-group-target="radio">Basic</div>
 *     <!-- more radios; exactly one navigable radio has tabindex=0 -->
 *     <input type="hidden" data-stimeo--radio-group-target="field" />
 *   </div>
 *
 * Implements the WAI-ARIA APG **Radio Group** pattern. Use this only for custom
 * radios; native `<input type="radio">` already owns its activation and form
 * semantics and should be preferred when its appearance is sufficient.
 *
 * `change` dispatches `{ value: string, radio: HTMLElement, reason: StateReason }`;
 * `reconcile` dispatches `{ value: string, radio: HTMLElement | null }`.
 *
 * User `change` reports compare the resulting state with the last published
 * state. A pending page write handled in the same script joins that confirmation;
 * a browser-delivered listener may settle it first as `reconcile`. Confirming
 * the last published state reports nothing.
 *
 * A synchronous subscriber that confirms another state replaces reports still
 * pending for the outer confirmation. Reading state or confirming it unchanged
 * does not replace them. A listener that disconnects the controller ends the
 * reports still pending. An event already being dispatched cannot be recalled.
 *
 * @remarks
 * Behavior only — selection is exposed through `aria-checked`, the single Tab
 * stop through roving `tabindex` (`RovingTabindex`); the consumer styles
 * those attributes. Selection follows focus for enabled radios.
 *
 * A supported radio is either `<button type="button">` or a non-interactive
 * host such as `<div role="radio">`. Other native interactive elements retain
 * their activation model and this controller stands down on them.
 *
 * Click and keydown handling are delegated from the group, so radios added at
 * runtime need only the `radio` target. Per-radio `data-action` bindings to
 * {@link select} and {@link onKeydown} are also accepted and are idempotent when
 * combined with delegation.
 *
 * Behavior provided:
 * - Click selects a radio.
 * - `ArrowDown` and the forward horizontal arrow (`ArrowRight`, or `ArrowLeft` when the
 *   group is right-to-left) select the next; `ArrowUp` and the backward one the previous
 *   (wrapping); `Home`/`End` the first/last; `Space` selects the focused radio.
 * - Native-disabled and hidden radios are skipped. ARIA-disabled radios remain
 *   discoverable in the roving order but cannot be selected.
 * - The selected radio's `data-value` is mirrored to the optional hidden field.
 *   `stimeo--radio-group:change` reports a confirmation — a user's choice or a
 *   `select()` call — whose radio or submitted value differs from the last
 *   published selection.
 * - `stimeo--radio-group:reconcile` reports a selection the page moved instead —
 *   a removed selection, first-wins over duplicate checked state, a morph that
 *   checks another radio, or a new `data-value` on the selected radio that moves
 *   the submitted value — carrying the selection detail without `reason`, once per batch
 *   and never on connect. The hidden field's native `change` stays reserved for
 *   those confirmations, so form automation never sees a repair as one.
 */
export class RadioGroupController extends Controller<HTMLElement> {
  readonly #moves = new MoveCounter();
  #connectionGeneration = 0;
  #moveToken = 0;
  static override targets = ["radio", "field"];
  static actions = ["onKeydown", "select"] as const;
  static events = ["change", "reconcile"] as const;

  declare readonly radioTargets: HTMLElement[];
  declare readonly fieldTarget: HTMLInputElement;
  declare readonly hasFieldTarget: boolean;

  readonly #roving = new RovingTabindex(() => this.#managedTargets);
  readonly #reconcile = new MorphRenderWatcher(() => this.#reconcileDom());
  readonly #handledEvents = new WeakSet<Event>();
  readonly #managedRadios = new Set<HTMLElement>();
  readonly #originalTabindex = new Map<HTMLElement, string | null>();
  readonly #ownedChecked = new Map<HTMLElement, string>();
  readonly #writtenTabindex = new WeakMap<HTMLElement, string | null>();
  readonly #observer = new AuthoredMutationWatcher((records) =>
    this.#handleMutationRecords(records),
  );
  /** The selection last published: read on connect, then committed or reported. */
  #committed: Selection = NO_SELECTION;
  #connected = false;
  #lastOrder: HTMLElement[] = [];
  #focusedRadio: HTMLElement | null = null;
  #pendingFocusIndex: number | null = null;
  #preferChecked = false;

  /** The listeners of one connection, released together on `disconnect()`. */
  readonly #listeners = new ListenerSet();

  /**
   * Normalizes authored selection, establishes the APG Tab entry point, and
   * reflects derived form state without reporting a user edit.
   */
  override connect(): void {
    this.#releaseDefaultsRewrittenWhileAway();
    const currentRadios = new Set(this.radioTargets);
    for (const radio of this.#managedRadios) {
      if (!currentRadios.has(radio)) this.#releaseRadio(radio);
    }
    for (const radio of this.radioTargets) this.#reconcileHost(radio, false);
    this.#normalizeSelection();
    this.#ensureTabStop(true);
    this.#reflectField(this.#selectedRadio);
    this.#committed = this.#settled;
    this.#lastOrder = this.#managedTargets;

    this.#listeners.add(this.element, "click", this.#onClickCapture, { capture: true });
    this.#listeners.add(this.element, "keydown", this.#onKeydownCapture, { capture: true });
    this.#listeners.add(this.element, "click", this.#onClick);
    this.#listeners.add(this.element, "keydown", this.#onKeydown);
    this.#listeners.add(this.element, "focusin", this.#onFocusin);
    this.#listeners.add(this.element, "focusout", this.#onFocusout);
    this.#connected = true;
    this.#preferChecked = false;
    this.#reconcile.observe(this.element);
    this.#observeMutations();
  }

  /** Releases every listener, observer, and queued pass while retaining live DOM state. */
  override disconnect(): void {
    this.#connectionGeneration += 1;
    this.#connected = false;
    this.#reconcile.disconnect();
    this.#listeners.dispose();
    this.#observer.disconnect();
    this.#focusedRadio = null;
    this.#pendingFocusIndex = null;
    this.#committed = NO_SELECTION;
    this.#preferChecked = false;
  }

  /** Removes a newly connected radio from the Tab sequence before batch reconciliation. */
  radioTargetConnected(radio: HTMLElement): void {
    if (this.#connected === false) return;
    this.#reconcileHost(radio, true);
    this.#preferChecked = true;
    this.#reconcile.schedule();
  }

  /** Releases target-only attributes and repairs selection, form state, focus, and roving. */
  radioTargetDisconnected(radio: HTMLElement): void {
    if (!this.#connected) return;
    if (this.#focusedRadio === radio) {
      // A radio that arrived and left inside one batch is absent from the saved
      // order; `-1` then makes the search start from the top, which is where a
      // group with no position to return to belongs.
      this.#pendingFocusIndex = this.#lastOrder.indexOf(radio);
      this.#focusedRadio = null;
    }
    this.#releaseRadio(radio);
    this.#preferChecked = true;
    this.#reconcile.schedule();
  }

  /** Reflects the current selection into a field added or replaced at runtime. */
  fieldTargetConnected(): void {
    this.#reconcile.schedule();
  }

  /** Reconciles after a field target is removed from a retained group. */
  fieldTargetDisconnected(): void {
    this.#reconcile.schedule();
  }

  /** Selects the action's radio. Per-radio action wiring is optional. */
  select(source: Event | HTMLElement): void {
    const { event, host, origin, reason } = actionSource(source);
    const radio = host?.closest<HTMLElement>(targetSelector(this.identifier, "radio"));
    if (!radio || !this.radioTargets.includes(radio)) return;
    if (origin && origin.closest(`[data-controller~="${this.identifier}"]`) !== this.element)
      return;
    if (event?.defaultPrevented || (event && !this.#ownsEventTarget(origin))) return;
    if (event) this.#handledEvents.add(event);
    this.#selectRadio(radio, { focus: false }, reason);
  }

  /** Applies the APG key map to the action's radio. Per-radio action wiring is optional. */
  onKeydown(event: KeyboardEvent): void {
    const radio = event.currentTarget as HTMLElement | null;
    if (!radio || !this.radioTargets.includes(radio)) return;
    this.#handleKeydown(event, radio);
  }

  /** Blocks disabled pointer activation before per-radio and consumer handlers run. */
  readonly #onClickCapture = (event: MouseEvent): void => {
    const radio = this.#radioForEventTarget(event.target);
    if (!radio || !this.#isSupportedHost(radio) || !this.#isActivationDisabled(radio)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /** Blocks disabled activation and suppresses a button's non-APG Enter activation. */
  readonly #onKeydownCapture = (event: KeyboardEvent): void => {
    if (event.key !== " " && event.key !== "Enter") return;
    const radio = this.#radioForEventTarget(event.target);
    if (!radio || !this.#isSupportedHost(radio)) return;
    if (
      this.#isActivationDisabled(radio) ||
      (event.key === "Enter" && radio instanceof HTMLButtonElement)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  /** Delegated click path for static and runtime-added radios without actions. */
  readonly #onClick = (event: MouseEvent): void => {
    if (this.#handledEvents.delete(event) || event.defaultPrevented) return;
    const radio = this.#radioForEventTarget(event.target);
    if (radio) this.#selectRadio(radio, { focus: false });
  };

  /** Delegated keydown path for static and runtime-added radios without actions. */
  readonly #onKeydown = (event: KeyboardEvent): void => {
    const radio = this.#radioForEventTarget(event.target);
    if (radio) this.#handleKeydown(event, radio);
  };

  /** Keeps programmatic and pointer focus as the group's current roving position. */
  readonly #onFocusin = (event: FocusEvent): void => {
    const radio = this.#radioForEventTarget(event.target);
    if (!radio || !this.#isNavigable(radio)) return;
    this.#focusedRadio = radio;
    this.#setActive(radio);
  };

  /** Distinguishes ordinary focus departure from focus lost because its radio was removed. */
  readonly #onFocusout = (event: FocusEvent): void => {
    const radio = this.#radioForEventTarget(event.target);
    if (!radio) return;
    queueMicrotask(() => {
      if (!this.#connected || this.#focusedRadio !== radio || !radio.isConnected) return;
      const active = document.activeElement;
      if (!(active instanceof Node) || !radio.contains(active)) this.#focusedRadio = null;
    });
  };

  /** Applies navigation and Space activation to one supported radio. */
  #handleKeydown(event: KeyboardEvent, radio: HTMLElement): void {
    if (event.defaultPrevented || !this.#isSupportedHost(radio)) return;
    if (event.isComposing || isReservedArrowChord(event)) return;
    if ((event.key === "Home" || event.key === "End") && hasModifierChord(event)) return;

    if (event.key === " ") {
      if (this.#isActivationDisabled(radio)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (radio instanceof HTMLButtonElement) {
        if (event.repeat) event.preventDefault();
        return;
      }
      event.preventDefault();
      if (!event.repeat) this.#selectRadio(radio, { focus: true });
      return;
    }

    const items = this.#managedTargets;
    const current = items.indexOf(radio);
    const step = logicalArrowStep(event.key, this.element);
    let destination: HTMLElement | undefined;
    if (step !== 0) {
      destination = this.#nextNavigable(current, step);
    } else if (event.key === "Home") {
      destination = this.#navigableRadios[0];
    } else if (event.key === "End") {
      const navigable = this.#navigableRadios;
      destination = navigable[navigable.length - 1];
    } else {
      return;
    }

    event.preventDefault();
    if (!destination) return;
    if (this.#isActivationDisabled(destination)) this.#setActive(destination, true);
    else this.#selectRadio(destination, { focus: true });
  }

  /**
   * Applies the selection and its mirror before focus or reporting can re-enter.
   * Identity and submitted value are compared with the last published selection.
   */
  #selectRadio(
    radio: HTMLElement,
    { focus }: { focus: boolean },
    reason: StateReason = "user",
  ): void {
    if (!this.#isSupportedHost(radio) || this.#isActivationDisabled(radio)) return;
    const generation = this.#connectionGeneration;
    const value = this.#radioValue(radio);
    const changed = this.#committed.radio !== radio || this.#committed.value !== value;
    for (const item of this.#managedTargets) this.#setChecked(item, item === radio);
    this.#committed = { radio, value };
    if (changed) this.#moveToken = this.#moves.record();
    const token = this.#moveToken;
    this.#setActive(radio);
    const fieldChanged = this.#writeField(value);
    this.#handleMutationRecords(this.#observer.take());
    if (focus) radio.focus();
    if (
      generation !== this.#connectionGeneration ||
      !this.#connected ||
      !this.#moves.isLatest(token)
    )
      return;
    if (changed && fieldChanged) commitField(this.fieldTarget);
    if (
      changed &&
      generation === this.#connectionGeneration &&
      this.#connected &&
      this.#moves.isLatest(token)
    ) {
      this.dispatch("change", { detail: { value, radio, reason } });
    }
  }

  /** Reconciles target ownership, selection, roving, focus continuity, and form state. */
  #reconcileDom(): void {
    this.#handleMutationRecords(this.#observer.take());
    for (const radio of this.radioTargets) this.#reconcileHost(radio, true);
    this.#normalizeSelection();

    const pendingFocusIndex = this.#pendingFocusIndex;
    this.#pendingFocusIndex = null;
    let destination: HTMLElement | null = null;
    if (pendingFocusIndex !== null) {
      destination = this.#nearestNavigable(pendingFocusIndex);
      this.#setActive(destination);
    } else {
      this.#ensureTabStop(this.#preferChecked);
    }
    this.#reflectField(this.#selectedRadio);
    this.#lastOrder = this.#managedTargets;
    this.#preferChecked = false;
    this.#handleMutationRecords(this.#observer.take());

    const settled = this.#settled;
    const committed = this.#committed;
    const changed = settled.radio !== committed.radio || settled.value !== committed.value;
    this.#committed = settled;
    const token = this.#moveToken;
    destination?.focus();
    if (!this.#connected || !this.#moves.isLatest(token)) return;
    if (changed) {
      this.dispatch("reconcile", { detail: { value: settled.value, radio: settled.radio } });
    }
  }

  /** Begins or ends ownership according to a radio's current host semantics. */
  #reconcileHost(radio: HTMLElement, dropFromTabSequence: boolean): void {
    if (!this.#isSupportedHost(radio)) {
      this.#releaseRadio(radio);
      return;
    }
    if (!this.#managedRadios.has(radio)) {
      this.#managedRadios.add(radio);
      this.#originalTabindex.set(radio, radio.getAttribute("tabindex"));
      if (dropFromTabSequence) this.#writeTabindex(radio, -1);
    }
    this.#normalizeChecked(radio);
  }

  /** Restores only defaults owned while an element belonged to this group. */
  #releaseRadio(radio: HTMLElement): void {
    this.#managedRadios.delete(radio);
    const original = this.#originalTabindex.get(radio);
    if (original === null) {
      this.#writeAttribute(radio, "tabindex", null);
    } else if (original !== undefined) {
      this.#writeAttribute(radio, "tabindex", original);
    }
    this.#originalTabindex.delete(radio);
    if (this.#ownedChecked.delete(radio)) {
      this.#writeAttribute(radio, "aria-checked", null);
    }
  }

  /** Supplies a missing checked state and normalizes invalid ARIA tokens. */
  #normalizeChecked(radio: HTMLElement): void {
    const value = radio.getAttribute("aria-checked");
    if (value === null) {
      this.#ownedChecked.set(radio, "false");
      this.#setChecked(radio, false);
    } else if (value !== "true" && value !== "false") {
      this.#setChecked(radio, false);
    }
  }

  /** Makes the first DOM-ordered checked radio the sole checked radio. */
  #normalizeSelection(): void {
    let found = false;
    for (const radio of this.#managedTargets) {
      if (!this.#isChecked(radio)) continue;
      if (!found) found = true;
      else this.#setChecked(radio, false);
    }
  }

  /** Keeps one navigable Tab stop, preferring checked state when selection changed externally. */
  #ensureTabStop(preferChecked: boolean): void {
    const navigable = this.#navigableRadios;
    let active: HTMLElement | undefined;
    if (!preferChecked) {
      const current = this.#managedTargets.filter(
        (radio) => radio.tabIndex === 0 && this.#isNavigable(radio),
      );
      if (current.length === 1) active = current[0];
    }
    const selected = this.#selectedRadio;
    if (!active && selected && this.#isNavigable(selected)) active = selected;
    active ??= navigable[0];
    this.#setActive(active ?? null);
  }

  /**
   * The surviving navigable radio closest to a position in the order captured
   * before the removal: the first one at or after it, else the last one before it.
   * The saved position and this search read the same population, so a `hidden` or
   * natively disabled sibling never shifts the destination past its neighbor.
   */
  #nearestNavigable(index: number): HTMLElement | null {
    const navigable = new Set(this.#navigableRadios);
    for (let i = index; i < this.#lastOrder.length; i++) {
      const radio = this.#lastOrder[i];
      if (radio && navigable.has(radio)) return radio;
    }
    for (let i = index - 1; i >= 0; i--) {
      const radio = this.#lastOrder[i];
      if (radio && navigable.has(radio)) return radio;
    }
    // Only radios added in the same batch survive; fall back to document order.
    return this.#navigableRadios[0] ?? null;
  }

  /** Moves through full DOM order until a navigable radio is found. */
  #nextNavigable(fromIndex: number, delta: 1 | -1): HTMLElement | undefined {
    const items = this.#managedTargets;
    let index = fromIndex;
    for (let step = 0; step < items.length; step++) {
      index = rovingMove(index, items.length, delta, "wrap");
      const candidate = items[index];
      if (candidate && this.#isNavigable(candidate)) return candidate;
    }
    return undefined;
  }

  /** Assigns the single roving Tab stop and optionally moves DOM focus. */
  #setActive(radio: HTMLElement | null, focus = false): void {
    const items = this.#managedTargets;
    const index = items.indexOf(radio as HTMLElement);
    this.#roving.setActive(index);
    for (const item of items) {
      this.#observer.claimCurrent(item, "tabindex");
      this.#writtenTabindex.set(item, item.getAttribute("tabindex"));
    }
    if (focus) radio?.focus();
  }

  /** Finds this group's radio containing an event target, excluding nested groups. */
  #radioForEventTarget(target: EventTarget | null): HTMLElement | null {
    if (!this.#ownsEventTarget(target)) return null;
    return ownerOf(this.radioTargets, target);
  }

  /** Whether the closest element around a target carrying this identifier is this group. */
  #ownsEventTarget(target: EventTarget | null): boolean {
    return (
      target instanceof Element &&
      target.closest(`[data-controller~="${this.identifier}"]`) === this.element
    );
  }

  /** Hosts whose activation model can be owned without conflicting native behavior. */
  #isSupportedHost(radio: HTMLElement): boolean {
    if (radio instanceof HTMLButtonElement) return radio.type === "button";
    return !isInteractiveHost(radio);
  }

  /** Radios eligible for roving focus; ARIA-disabled deliberately remains eligible. */
  #isNavigable(radio: HTMLElement): boolean {
    if (!this.#isSupportedHost(radio) || this.#isHidden(radio)) return false;
    if (!(radio instanceof HTMLButtonElement)) return true;
    return !radio.disabled && !inheritsFieldsetDisabled(radio);
  }

  /** Whether hidden applies on the path from a radio up to the group root. */
  #isHidden(radio: HTMLElement): boolean {
    let current: HTMLElement | null = radio;
    while (current && current !== this.element) {
      if (current.hasAttribute("hidden")) return true;
      current = current.parentElement;
    }
    return false;
  }

  /** Whether ARIA, visibility, or native HTML semantics suppress selection. */
  #isActivationDisabled(radio: HTMLElement): boolean {
    if (!this.#isNavigable(radio)) return true;
    let current: HTMLElement | null = radio;
    while (current) {
      if (current.getAttribute("aria-disabled") === "true") return true;
      current = current.parentElement;
    }
    return false;
  }

  /** Distinguishes external retained-element changes from this controller's own writes. */
  #handleMutationRecords(records: MutationRecord[]): void {
    let external = false;
    for (const record of records) {
      const target = record.target as HTMLElement;
      const attribute = record.attributeName;
      if (attribute === "aria-checked") {
        this.#ownedChecked.delete(target);
        this.#preferChecked = true;
        external = true;
      } else if (attribute === "tabindex") {
        const value = target.getAttribute(attribute);
        if (this.#managedRadios.has(target)) this.#originalTabindex.set(target, value);
        external = true;
      } else {
        external = true;
      }
    }
    if (external) this.#reconcile.schedule();
  }

  /** Watches retained state, target membership, and inherited host semantics. */
  #observeMutations(): void {
    this.#observer.observe(this.element, {
      subtree: true,
      childList: true,
      attributes: OBSERVED_ATTRIBUTES,
      ancestorAttributes: ["contenteditable", "disabled"],
    });
  }

  /** Releases defaults and restores authored tab stops rewritten during a disconnected lifetime. */
  #releaseDefaultsRewrittenWhileAway(): void {
    for (const [radio, written] of this.#ownedChecked) {
      const current = radio.getAttribute("aria-checked");
      if (current !== null && current !== written) this.#ownedChecked.delete(radio);
    }
    for (const radio of this.#managedRadios) {
      const written = this.#writtenTabindex.get(radio);
      const current = radio.getAttribute("tabindex");
      if (current !== written) this.#originalTabindex.set(radio, current);
    }
  }

  /** Supported targets currently managed by the roving primitive. */
  get #managedTargets(): HTMLElement[] {
    return this.radioTargets.filter((radio) => this.#managedRadios.has(radio));
  }

  /** Managed radios eligible for the roving Tab stop. */
  get #navigableRadios(): HTMLElement[] {
    return this.#managedTargets.filter((radio) => this.#isNavigable(radio));
  }

  /** The currently checked managed radio, if any. */
  get #selectedRadio(): HTMLElement | undefined {
    return this.#managedTargets.find((radio) => this.#isChecked(radio));
  }

  /** The current selection as the group publishes it. */
  get #settled(): Selection {
    const radio = this.#selectedRadio;
    return radio ? { radio, value: this.#radioValue(radio) } : NO_SELECTION;
  }

  /** Whether a radio is currently checked. */
  #isChecked(radio: HTMLElement): boolean {
    return radio.getAttribute("aria-checked") === "true";
  }

  /** Reflects checked state while distinguishing controller writes from authored morphs. */
  #setChecked(radio: HTMLElement, checked: boolean): void {
    const value = checked ? "true" : "false";
    if (this.#ownedChecked.has(radio)) this.#ownedChecked.set(radio, value);
    this.#writeAttribute(radio, "aria-checked", value);
  }

  /** Writes a roving value before the radio is visible to the shared primitive. */
  #writeTabindex(radio: HTMLElement, value: number): void {
    const serialized = String(value);
    this.#writtenTabindex.set(radio, serialized);
    this.#writeAttribute(radio, "tabindex", serialized);
  }

  /** Claims one actual write without hiding pending authored records. */
  #writeAttribute(target: HTMLElement, name: string, value: string | null): void {
    setAttributeIfChanged(target, name, value, () => this.#observer.claim(target, name, value));
  }

  /** Mirrors the selected value or the empty state to the optional hidden field. */
  #reflectField(radio: HTMLElement | undefined): void {
    const value = radio ? this.#radioValue(radio) : "";
    this.#writeField(value);
  }

  /** A radio's submitted value (`data-value`, defaulting to empty). */
  #radioValue(radio: HTMLElement): string {
    return radio.getAttribute("data-value") ?? "";
  }
  /** Claims the attribute record a hidden field's changed reflected value produces. */
  #writeField(value: string): boolean {
    if (!this.hasFieldTarget) return false;
    const changed = writeField(this.fieldTarget, value);
    if (changed && this.fieldTarget.type === "hidden")
      this.#observer.claimCurrent(this.fieldTarget, "value");
    return changed;
  }
}
